import React, { useMemo, useState } from 'react';
import { Select, Alert, Spin, Empty, Button, Drawer } from "antd";
import { without } from "lodash";
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import StringFormatter from '../../shared/stringFormatter';
import { driverAssignedEmailTemplate, driverUnAssignedEmailTemplate } from "../../shared/emailTemplates";
import FormItem from '../FormItem/FormItem';
import Form from '../Form/Form';
import FormButtons from '../FormButtons/FormButtons';
import Fieldset from '../FormFieldset/FormFieldset';
import { sendEmail } from '../../api/emailClient';
import { isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty } from '../../shared/objectUtils';
import { sendLoadNotification } from '../../api/data';
import logger from '../../shared/logger';
import NewDriver from '../NewDriver/NewDriver';
import { selectListIsLoading, selectListRecords } from '../../store/utility';

const { Option } = Select;
const stringFormatter = new StringFormatter();

const ChangeLoadDriver = ({ load, carrierId, driver, cancel }) => {
    //#region constants

    const fullWidth = global.window.innerWidth;

    //#endregion
    //#region useForms

    const methods = useForm({ mode: 'all', reValidateMode: 'onChange', criteriaMode: 'all', shouldFocusError: true, shouldUnregister: true });

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const isLoading = useSelector(state => state.loads.isRecordUpdateLoading);
    const error = useSelector(state => state.loads.updateRecordError);
    const isLoadingAccountUsers = useSelector(state => selectListIsLoading(state.accountUsers.lists, `${carrierId}_DRIVERS`));
    const accountUsers = useSelector(state => selectListRecords(state.accountUsers.lists, `${carrierId}_DRIVERS`));

    //#endregion
    //#region useStates

    const [oldDriverAccountUserId, setOldDriverAccountUserId] = useState(null);
    const [newDriverAccountUserId, setNewDriverAccountUserId] = useState(null);
    const [showNewEntity, setShowNewEntity] = useState(false);

    //#endregion
    //#region toggles

    const toggleNewEntity = () => {
        setShowNewEntity(!showNewEntity);
    };

    //#endregion
    //#region onSubmit and onCancel

    const onSubmit = (data) => {
        if (isObjectNotEmpty(data)) {
            //console.log(data);

            const updatedDriverIds = without(load.driverIds, driver.id);
            //console.log(updatedDriverIds);

            // check to see if it is a duplicate and if it is found in the updatedDriverIds array already, throw an error
            if (isListNotEmpty(updatedDriverIds)) {
                if (updatedDriverIds.find(i => i === data.accountUserId) !== undefined) {
                    methods.setError("accountUserId", { type: "validate", message: "The driver you chose is already assigned to this load." });
                    return;
                } else {
                    methods.clearErrors("accountUserId");
                }
            }

            let updatedDriverIdsWithNewDriver = [...updatedDriverIds, data.accountUserId];
            //console.log(updatedDriverIdsWithNewDriver);

            // removes duplicates
            const updatedDriverIdsWithNewDriver2 = [...new Set(updatedDriverIdsWithNewDriver)];
            //console.log(updatedDriverIdsWithNewDriver2);
            let payload = {};
            payload.driverIds = updatedDriverIdsWithNewDriver2;
            //console.log(payload);
            setOldDriverAccountUserId(driver.id);
            setNewDriverAccountUserId(data.accountUserId);
            dispatch(actionCreators.updateLoad(load.id, payload));
        } else {
            onCancel();
        }
    };

    const onCancel = () => {
        dispatch(actionCreators.updateLoadCancel());
        cancel();
    };

    //#endregion
    //#region email and notification methods

    const sendEmailToDriver = (accountUserId) => {
        if (isListNotEmpty(accountUsers) && isStringNotEmpty(accountUserId)) {
            let accountUser = accountUsers.find(d => d.id === accountUserId);
            if (isObjectNotEmpty(accountUser) && isObjectNotEmpty(accountUser.driver)) {
                if (accountUser.driver.isApproved === undefined || accountUser.driver.isApproved === null || accountUser.driver.isApproved === false) {
                    const { body, subject } = driverAssignedEmailTemplate(accountUser.firstName, load.irisId)
                    sendEmail(subject, body, accountUser.email).then(res => {
                        //console.log(res);
                    }).catch(err => {
                        logger.logDebugEvent('ChangeLoadDriver.js', err.message, true);
                    });
                }
            }
        }
    };

    const sendEmailToOldDriver = (accountUserId) => {
        if (isListNotEmpty(accountUsers) && isStringNotEmpty(accountUserId)) {
            let accountUser = accountUsers.find(d => d.id === accountUserId);
            if (isObjectNotEmpty(accountUser) && isObjectNotEmpty(accountUser.driver)) {
                if (accountUser.driver.isApproved === undefined || accountUser.driver.isApproved === null || accountUser.driver.isApproved === false) {
                    const { body, subject } = driverUnAssignedEmailTemplate(accountUser.firstName, load.irisId)
                    sendEmail(subject, body, accountUser.email).then(res => {
                        //console.log(res);
                    }).catch(err => {
                        logger.logDebugEvent('ChangeLoadDriver.js', err.message, true);
                    });
                }
            }
        }
    };

    const sendNotificationToDriver = (accountUserId) => {
        if (isListNotEmpty(accountUsers) && isStringNotEmpty(accountUserId)) {
            let accountUser = accountUsers.find(d => d.id === accountUserId);
            if (isObjectNotEmpty(accountUser) && isObjectNotEmpty(accountUser.driver)) {
                let newNotification = {
                    message: `Hi ${accountUser.firstName}, you have been assigned to a new load: ${load.irisId}. Please open up the IRIS Freight app for more details.`,
                    loadId: load.id,
                    loadIrisId: load.irisId,
                    saveOnly: false,
                    subject: `${load.irisId} You have been assigned to a new load`,
                    severity: "CRITICAL",
                    userIds: [accountUser.userId],
                    loadStatus: load.loadStatus,
                    eventType: 'DRIVER_ASSIGNED'
                };

                sendLoadNotification(newNotification).then(res => {
                    //console.log(res);
                }).catch(err => {
                    logger.logDebugEvent('ChangeLoadDriver.js', err.message, true);
                });
            }
        }
    };

    const sendNotificationToOldDriver = (accountUserId) => {
        if (isListNotEmpty(accountUsers) && isStringNotEmpty(accountUserId)) {
            let accountUser = accountUsers.find(d => d.id === accountUserId);
            if (isObjectNotEmpty(accountUser) && isObjectNotEmpty(accountUser.driver)) {
                let newNotification = {
                    message: `Hi ${accountUser.firstName}, you are no longer assigned to load: ${load.irisId}. Please open up the IRIS Freight app for more details.`,
                    loadId: load.id,
                    loadIrisId: load.irisId,
                    saveOnly: false,
                    subject: `${load.irisId} You have been unassigned from a load`,
                    severity: "CRITICAL",
                    userIds: [accountUser.userId],
                    loadStatus: load.loadStatus,
                    eventType: 'DRIVER_UNASSIGNED'
                };

                sendLoadNotification(newNotification).then(res => {
                    //console.log(res);
                }).catch(err => {
                    logger.logDebugEvent('ChangeLoadDriver.js', err.message, true);
                });
            }
        }
    };

    //#endregion
    //#region useMemos

    useMemo(() => {
        if (isStringNotEmpty(carrierId)) {
            dispatch(actionCreators.fetchAccountUserList(`${carrierId}_DRIVERS`, { page: 1, size: 1000000, isDriver: true, accountId: carrierId, isDeleted: false }));
        }
    }, [carrierId]);

    useMemo(() => {
        // clear any previous errors if this is a new form
        dispatch(actionCreators.updateLoadErrorClear());
    }, []);

    useMemo(() => {
        if (isLoading !== null && isLoading === false && error === null && isNotNullOrUndefined(oldDriverAccountUserId) && isNotNullOrUndefined(newDriverAccountUserId) && isObjectNotEmpty(load)) {
            sendNotificationToDriver(newDriverAccountUserId);
            sendEmailToDriver(newDriverAccountUserId)
            sendNotificationToOldDriver(oldDriverAccountUserId);
            sendEmailToOldDriver(oldDriverAccountUserId);

            if (load.loadStatus === 'IN_TRANSIT' || load.loadStatus === 'AT_STOP') {
                dispatch(actionCreators.markDriverAvailable(oldDriverAccountUserId));
                dispatch(actionCreators.markDriverUnavailable(newDriverAccountUserId));
            }

            onCancel();
        }
    }, [isLoading, error, oldDriverAccountUserId, newDriverAccountUserId, load]);

    //#endregion
    //#region styles

    const formItemLayout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    };

    //#endregion

    return (
        <>
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Spin style={{ height: '100%', width: '100%' }} size="large" spinning={(isLoading === true && error === null) || isLoadingAccountUsers === true}>
                        <Fieldset legend="Change the Driver Assigned to this Load">
                            <FormItem {...formItemLayout} label="Choose a Driver" required
                                render={({ onChange, onBlur, value, name, ref }) => (
                                    <Select
                                        placeholder="Please Search and Select a Driver"
                                        allowClear={true}
                                        style={{ width: '100%' }}
                                        virtual={false}
                                        loading={isLoadingAccountUsers === true}
                                        onBlur={onBlur}
                                        onChange={(selected) => { onChange(selected); }}
                                        value={value}
                                        name={name}
                                        showSearch={true}
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sorry, but we couldn't find any Drivers for you to choose from." />}
                                        ref={ref}
                                    >
                                        {accountUsers.map(d => <Option value={d.id} key={d.id}>{d.firstName + ' ' + d.lastName} - Currently Available: {stringFormatter.toYesNo(d.driver.isAvailable)}</Option>)}
                                    </Select>
                                )}
                                rules={{ required: 'Required Field' }}
                                name="accountUserId"
                                defaultValue={driver.id}
                            />
                            <br />
                            <b>Driver Doesn't Exist Yet?</b>
                            <br />
                            <Button type='primary' onClick={toggleNewEntity}>Add New Driver to Account</Button>
                        </Fieldset>
                        {error && <Alert message={`${error}`} type="error" />}
                    </Spin>
                    <FormButtons cancel={onCancel} disabled={isLoading === true && error === null} submitText="Change Driver" />
                </Form>
            </FormProvider>
            <Drawer
                title={'Add New Driver'}
                width={fullWidth > 720 ? fullWidth / 2 : 360}
                onClose={toggleNewEntity}
                visible={showNewEntity}
                bodyStyle={{ paddingBottom: 80 }}
                style={{ zIndex: 1000 }}
                destroyOnClose={true}
                closable={false}
                maskClosable={false}
            >
                <NewDriver cancel={toggleNewEntity} accountId={carrierId} />
            </Drawer>
        </>
    );
};

export default ChangeLoadDriver;